import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import Icon from "@/components/Icon";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DownloadSimple, Copy, Printer, QrCode, Globe, FloppyDisk } from "@phosphor-icons/react";

const normalizeUrl = (raw) => {
  let u = (raw || "").trim();
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) u = "https://" + u;
  return u.replace(/\/+$/, "") + "/";
};

export default function ShareGuests({ property, onSaved }) {
  const wrapRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [urlInput, setUrlInput] = useState(property?.production_url || "");
  const [savingUrl, setSavingUrl] = useState(false);
  const [includeCode, setIncludeCode] = useState(false);

  const productionUrl = normalizeUrl(property?.production_url);
  const isActive = Boolean(productionUrl);
  const guestUrl = isActive ? productionUrl : "";
  const [code, setCode] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/guest-codes");
        const codes = data.codes || [];
        const guest = codes.find((c) => (c.category || "guest") === "guest") || codes[0];
        setCode(guest?.code || "");
      } catch {
        /* leave blank */
      }
    })();
  }, []);

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      await api.put("/admin/property", { ...property, production_url: urlInput.trim() });
      toast.success(urlInput.trim() ? "Production address saved — QR code is now active" : "Production address cleared — QR code deactivated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingUrl(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(guestUrl);
      setCopied(true);
      toast.success("Guidebook link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const downloadPng = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "via-solana-guidebook-qr.png";
    a.click();
    toast.success("QR code downloaded");
  };

  const printCard = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>Welcome — ${property?.name || ""}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=Manrope:wght@400;600&display=swap');
        * { margin:0; box-sizing:border-box; }
        body { font-family:'Manrope',sans-serif; color:#2C3325; background:#F7F6F2;
               display:flex; align-items:center; justify-content:center; min-height:100vh; padding:40px; }
        .card { border:1px solid rgba(44,51,37,.2); padding:64px 56px; text-align:center; max-width:560px; background:#F7F6F2; }
        h1 { font-family:'Playfair Display',serif; font-size:38px; margin:0 0 16px; font-weight:500; line-height:1.15; }
        .scan { font-size:16px; color:rgba(44,51,37,.75); margin-bottom:40px; }
        img { width:240px; height:240px; }
        .code { margin-top:36px; }
        .code .lbl { text-transform:uppercase; letter-spacing:.2em; font-size:11px; color:rgba(44,51,37,.55); }
        .code .val { font-family:'Playfair Display',serif; font-size:34px; letter-spacing:.15em; margin-top:6px; }
        .hint { margin-top:32px; font-size:14px; color:rgba(44,51,37,.75); }
        .foot { margin-top:28px; font-size:12px; color:rgba(44,51,37,.55); }
      </style></head>
      <body onload="setTimeout(function(){window.print()},300)">
        <div class="card">
          <h1>Welcome to ${property?.name || "48205 Via Solana"}!</h1>
          <div class="scan">Scan the code below for the guest guidebook.</div>
          <img src="${dataUrl}" alt="QR code"/>
          ${includeCode && code
            ? `<div class="code"><div class="lbl">Guest Access Code</div><div class="val">${code}</div></div>`
            : `<div class="hint">Enter the access code provided with your booking confirmation.</div>`}
          <div class="foot">Check-in, WiFi, amenities &amp; house guide — all in one place</div>
        </div>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="share-panel">
      <div>
        <span className="cms-label text-accent">Share</span>
        <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Guest QR Code</h2>
        <p className="text-muted-foreground mt-3 text-sm max-w-md">
          Print this and place it inside the home. Guests scan it to open the guidebook,
          then enter the access code below.
        </p>
      </div>

      {/* Production address controls */}
      <div className="border border-border p-5 bg-secondary/40 space-y-3" data-testid="production-url-settings">
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-accent" />
          <p className="cms-label text-foreground/60">Production Address</p>
          <span
            data-testid="qr-status-badge"
            className={
              "ml-auto text-[11px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full " +
              (isActive ? "bg-foreground text-primary-foreground" : "bg-accent/15 text-accent")
            }
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {isActive
            ? "The QR code below permanently points to this address. It is safe to print and frame."
            : "The QR code stays inactive until you set the final live address, so a temporary preview link can never be printed by accident. Add it here once the guidebook is deployed."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://your-live-guidebook-address.com"
            data-testid="production-url-input"
            className="rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent flex-1"
          />
          <button
            onClick={saveUrl}
            disabled={savingUrl}
            data-testid="save-production-url-btn"
            className="inline-flex items-center justify-center gap-2 bg-foreground text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60"
          >
            <FloppyDisk size={15} weight="bold" />
            {savingUrl ? "Saving…" : isActive ? "Update" : "Activate"}
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-10 items-start">
        {isActive ? (
          <div ref={wrapRef} className="border border-border p-6 bg-background inline-block" data-testid="qr-code">
            <QRCodeCanvas
              value={guestUrl}
              size={220}
              level="M"
              fgColor="#2C3325"
              bgColor="#F7F6F2"
              includeMargin={false}
            />
          </div>
        ) : (
          <div
            className="border border-dashed border-border w-[268px] h-[268px] grid place-items-center text-center p-8"
            data-testid="qr-inactive-placeholder"
          >
            <div className="space-y-3">
              <QrCode size={48} className="mx-auto text-foreground/25" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                QR code inactive.<br />Set the production address above to activate it.
              </p>
            </div>
          </div>
        )}

        <div className="flex-1 space-y-6">
          <div>
            <p className="cms-label text-foreground/50">Guidebook Link</p>
            {isActive ? (
              <>
                <button
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                  className="mt-2 group flex items-center gap-2 text-foreground link-underline break-all text-left"
                >
                  <span className="text-sm">{guestUrl}</span>
                  <Copy size={15} className="shrink-0 opacity-60 group-hover:opacity-100" />
                </button>
                {copied && <span className="text-xs text-accent">Copied!</span>}
              </>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="link-inactive-note">
                Available once the QR code is active.
              </p>
            )}
          </div>

          <div>
            <p className="cms-label text-foreground/50">Guest Access Code</p>
            <p className="font-display text-3xl tracking-[0.12em] text-foreground mt-2">{code || "—"}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Manage per-guest codes under the <span className="text-foreground/70">Guidebook Logins</span> tab.
            </p>
          </div>

          {isActive && (
            <div className="space-y-4 pt-2">
              <label className="flex items-center gap-3 cursor-pointer text-sm text-foreground/80 max-w-sm" data-testid="include-code-wrap">
                <input
                  type="checkbox"
                  checked={includeCode}
                  onChange={(e) => setIncludeCode(e.target.checked)}
                  data-testid="include-code-toggle"
                  className="w-4 h-4 accent-[#CC7351]"
                />
                Print the guest access code on the card (leave off for a generic card that works for every guest)
              </label>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={downloadPng}
                  data-testid="download-qr-btn"
                  className="inline-flex items-center gap-2 bg-foreground text-primary-foreground pl-5 pr-6 py-3 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300"
                >
                  <DownloadSimple size={16} weight="bold" /> Download PNG
                </button>
                <button
                  onClick={printCard}
                  data-testid="print-qr-btn"
                  className="inline-flex items-center gap-2 border border-border text-foreground pl-5 pr-6 py-3 rounded-full text-sm font-semibold hover:bg-secondary transition-colors duration-300"
                >
                  <Printer size={16} weight="bold" /> Print card
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 border-l-2 border-accent pl-5 py-1 text-sm text-foreground/70 max-w-md">
        <Icon name="SunHorizon" size={20} weight="duotone" className="text-accent shrink-0 mt-0.5" />
        <p>
          {isActive
            ? "Tip: frame the printed card near the entry or kitchen so arriving guests find everything instantly."
            : "Tip: deploy the guidebook first, then enter the live address above — your framed QR will work from day one and never need reprinting."}
        </p>
      </div>
    </div>
  );
}
